import {
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Flex,
  Box,
  Spinner,
} from '@chakra-ui/react';
import { useState, useEffect } from 'react';
import { KText } from '@/ui-library/components/text';
import { ChevronRight, UsersPlus } from '@kula-ai/icons-core';
import { useHrisIntegrations } from '@/components/ats/hris/useHrisIntegrations';

interface HrisIntegration {
  id: string;
  label: string;
  type?: string;
  disabled?: boolean;
  loading?: boolean;
  added?: boolean;
}

interface HrisSubMenuProps {
  integrations?: HrisIntegration[];
  onIntegrationClick: (integration: HrisIntegration) => void;
}

export const HrisSubMenu = ({
  integrations = [],
  onIntegrationClick,
}: HrisSubMenuProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedIntegration, setSelectedIntegration] = useState<string | null>(
    null
  );
  const { renderIcon } = useHrisIntegrations();
  const singleIntegration = integrations.length === 1 ? integrations[0] : null;

  useEffect(() => {
    // Only close the menu if all loading is done AND the selectedIntegration is completed
    if (
      selectedIntegration &&
      !integrations.some((i) => i.loading || i.id === selectedIntegration)
    ) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- syncs menu-open state to async integration-loading completion; behaviour preserved during the TS migration
      setIsOpen(false);
      setSelectedIntegration(null);
    }
  }, [integrations, selectedIntegration]);

  const handleIntegrationClick = (integration) => {
    if (!integration.disabled) {
      setSelectedIntegration(integration.id);
      const updatedIntegration = { ...integration, loading: true };
      onIntegrationClick(updatedIntegration);
    }
  };

  const getLoadingText = (integration) => {
    const isKomboIntegration = integration.type?.includes('kombo');
    return isKomboIntegration
      ? 'Loading employee form'
      : `Adding to ${integration.label}`;
  };

  if (singleIntegration) {
    const isDisabled =
      singleIntegration.disabled ||
      singleIntegration.loading ||
      integrations.some((i) => i.loading);

    return (
      <Menu autoSelect={false}>
        <MenuButton
          as={MenuItem}
          display="flex"
          width="100%"
          gap="8px"
          _hover={{ fontWeight: 'semibold' }}
          px="12px"
          py="8px"
          onClick={() =>
            !isDisabled && handleIntegrationClick(singleIntegration)
          }
          opacity={isDisabled ? 0.5 : 1}
          cursor={isDisabled ? 'not-allowed' : 'pointer'}
          pointerEvents={isDisabled ? 'none' : 'auto'}
        >
          <Flex alignItems="center" width="100%" gap="8px">
            {renderIcon(singleIntegration)}
            <Flex justify="space-between" alignItems="center" width="100%">
              <Flex alignItems="center" gap="8px">
                <KText
                  size="sm-medium"
                  fontWeight="inherit"
                  color="secondary-900"
                >
                  {singleIntegration.loading
                    ? getLoadingText(singleIntegration)
                    : `Add to ${singleIntegration.label}`}
                </KText>
                {singleIntegration.loading && (
                  <Spinner size="sm" color="secondary-600" ml="8px" />
                )}
              </Flex>
              {singleIntegration.added && !singleIntegration.loading && (
                <Box px="6px" py="2px" bg="pastel-2-light" borderRadius="6px">
                  <KText size="sm-medium" color="pastel-2-dark">
                    Added
                  </KText>
                </Box>
              )}
            </Flex>
          </Flex>
        </MenuButton>
      </Menu>
    );
  }

  return (
    <Box position="relative">
      <Menu
        isOpen={isOpen}
        placement="auto"
        autoSelect={false}
        closeOnSelect={false}
        className="hris-sub-menu"
        data-test-id="hris-sub-menu"
        zIndex={9}
      >
        <MenuButton
          as={MenuItem}
          display="flex"
          width="100%"
          gap="8px"
          _hover={{ fontWeight: 'semibold' }}
          ml="6px"
          py="8px"
          onClick={() => setIsOpen((prev) => !prev)}
        >
          <Flex justify="space-between" width="100%" alignItems="center">
            <Flex alignItems="center" gap="8px">
              <UsersPlus size={16} className="kula-icon" />
              <KText
                size="sm-medium"
                fontWeight="inherit"
                color="secondary-900"
              >
                Add to HRIS
              </KText>
            </Flex>
            <ChevronRight size={14} />
          </Flex>
        </MenuButton>
        <MenuList minW="270px" borderRadius="md" boxShadow="lg" zIndex={9}>
          {integrations.map((integration) => {
            const isLoading =
              integration.loading && selectedIntegration === integration.id;
            const isDisabled =
              integration.disabled ||
              isLoading ||
              integrations.some((i) => i.loading);

            return (
              <MenuItem
                key={integration.id}
                onClick={() =>
                  !isDisabled && handleIntegrationClick(integration)
                }
                gap="8px"
                _hover={{ fontWeight: 'semibold' }}
                opacity={isDisabled ? 0.5 : 1}
                cursor={isDisabled ? 'not-allowed' : 'pointer'}
                pointerEvents={isDisabled ? 'none' : 'auto'}
              >
                {renderIcon(integration)}
                <Flex justify="space-between" align="center" width="100%">
                  <Flex align="center">
                    <KText>
                      {isLoading
                        ? getLoadingText(integration)
                        : `Add to ${integration.label}`}
                    </KText>
                    {isLoading && (
                      <Spinner size="sm" color="secondary-600" ml="8px" />
                    )}
                  </Flex>
                  {integration.added && !integration.loading && (
                    <Box
                      px="6px"
                      py="2px"
                      bg="pastel-2-light"
                      borderRadius="6px"
                    >
                      <KText size="sm-medium" color="pastel-2-dark">
                        Added
                      </KText>
                    </Box>
                  )}
                </Flex>
              </MenuItem>
            );
          })}
        </MenuList>
      </Menu>
    </Box>
  );
};
